import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend, getEmptyImage } from 'react-dnd-html5-backend';
import { ListGroup, ListGroupItem, Button } from 'reactstrap';
import { LuMenu, LuArrowUp, LuArrowDown, LuChevronsUp, LuChevronsDown } from 'react-icons/lu';

import 'bootstrap/dist/css/bootstrap.min.css';
import { accentUtils, from, newGuid } from '../services/HelperService';

// Base style constants.
const BASE_PADDING_VERTICAL = 0.5; // in rem
const BASE_PADDING_HORIZONTAL = 1; // in rem
const BASE_MARGIN_BOTTOM = 4; // in px
const BASE_FONT_SIZE = 1; // in rem

const ITEM_TYPE = 'LIST_ITEM';

const EmptyGroupDropTarget = ({ onDrop, listID }) => {
    const [{ isOver, canDrop }, drop] = useDrop({
        accept: ITEM_TYPE,
        canDrop: (draggedItem) => draggedItem.listID === listID,
        drop: (draggedItem, monitor) => {
            if (monitor.didDrop()) return;
            onDrop();
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    return (
        <div
            ref={drop}
            style={{
                minHeight: '60px',
                padding: '1rem',
                border: `2px dashed ${isOver && canDrop ? '#28a745' : '#e0e0e0'}`,
                borderRadius: '4px',
                backgroundColor: isOver && canDrop ? '#e9f7ef' : '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
            }}
        >
            {isOver && canDrop ? 'Release to drop' : 'Drag items here'}
        </div>
    );
};


const ReorderableListItem = ({
    idField,
    item,
    index,
    selected,
    toggleSelect,
    onDragStartItem,
    onDragEnd,
    onDropItem,
    itemRenderer,
    recentlyDropped,
    onItemChanged,
    onMoveUp,
    onMoveDown,
    onMoveToTop,
    onMoveToBottom,
    showMoveButtons,
    listID,
    handleRenderer,
    groupField,
}) => {
    const itemRef = useRef(null);
    const handleRef = useRef(null);
    const [dropIndicator, setDropIndicator] = useState(null); // 'above' or 'below'

    const [{ isDragging }, drag, preview] = useDrag({
        type: ITEM_TYPE,
        item: () => {
            onDragStartItem(index, selected);
            return { index, id: item[idField], listID };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (draggedItem, monitor) => {
            if (!monitor.didDrop()) {
                onDragEnd();
            }
        },
    });

    // Only the handle initiates the drag.
    drag(handleRef);

    // Hide the default preview on the handle.
    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    // Set the drag preview to the entire list item.
    const setPreviewRef = (node) => {
        itemRef.current = node;
        if (node) {
            preview(node);
        }
    };

    const handleItemRendererClick = (e) => {
        e.stopPropagation();
    };

    const [{ isOver }, drop] = useDrop({
        accept: ITEM_TYPE,
        canDrop: (draggedItem) => draggedItem.listID === listID,
        hover(draggedItem, monitor) {
            if (!itemRef.current) return;
            if (draggedItem.index === index) {
                setDropIndicator(null);
                return;
            }
            const rect = itemRef.current.getBoundingClientRect();
            const hoverMiddleY = (rect.bottom - rect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - rect.top;
            const newIndicator = hoverClientY < hoverMiddleY ? 'above' : 'below';
            setDropIndicator(newIndicator);
            draggedItem.dropIndicator = newIndicator;
        },
        drop(draggedItem) {
            const dropPos = draggedItem.dropIndicator;
            const insertionIndex = dropPos === 'above' ? index : index + 1;
            onDropItem(insertionIndex, item[groupField]);
            setDropIndicator(null);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver() && monitor.canDrop(),
        }),
    });
    drop(itemRef);

    // Compute dynamic styles.
    const currentPaddingVertical = BASE_PADDING_VERTICAL;
    const currentMarginBottom = BASE_MARGIN_BOTTOM;
    const currentFontSize = BASE_FONT_SIZE;

    return (
        <div ref={setPreviewRef} >
            <div
                onClick={() => toggleSelect(item[idField])}
                className={`d-flex align-items-center ${recentlyDropped ? 'bg-warning' : ''}`}
                style={{
                    opacity: isDragging ? 0.5 : 1,
                    position: 'relative',
                    transition: selected ? undefined : 'all 0.3s ease',
                    //padding: `${currentPaddingVertical}rem ${BASE_PADDING_HORIZONTAL}rem`,
                    marginBottom: `${currentMarginBottom}px`,
                    fontSize: `${currentFontSize}rem`,
                    border: selected ? '2px solid blue' : '1px solid #e0e0e0'
                }}
                id={`${idField}-${item[idField]}`}
            >
                {/* Drag handle */}
                {handleRenderer ? (
                    <div ref={handleRef} style={{ cursor: 'grab' }}>
                        {handleRenderer({ item, selected })}
                    </div>
                ) : (
                    <div ref={handleRef} style={{ cursor: 'grab', padding: "10px" }}>
                        <LuMenu size={20} />
                    </div>
                )}
                {/* Item content */}
                <div
                    style={{ flex: 1 }}
                    onClick={handleItemRendererClick}
                >
                    {itemRenderer({ item, selected, onChange: onItemChanged })}
                </div>
                {/* Additional move icons */}
                {showMoveButtons && (
                    <div className="d-flex align-items-center">
                        <Button
                            color="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveToTop && onMoveToTop(index);
                            }}
                        >
                            <LuChevronsUp size={20} />
                        </Button>
                        <Button
                            color="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveUp && onMoveUp(index);
                            }}
                        >
                            <LuArrowUp size={20} />
                        </Button>
                        <Button
                            color="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveDown && onMoveDown(index);
                            }}
                        >
                            <LuArrowDown size={20} />
                        </Button>
                        <Button
                            color="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveToBottom && onMoveToBottom(index);
                            }}
                        >
                            <LuChevronsDown size={20} />
                        </Button>
                    </div>
                )}
                {/* Visual drop indicators */}
                {isOver && dropIndicator === 'above' && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            backgroundColor: 'red',
                        }}
                    />
                )}
                {isOver && dropIndicator === 'below' && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            backgroundColor: 'red',
                        }}
                    />
                )}
            </div>
        </div>
    );
};


export const ReorderableList = ({
    idField,
    items: initialItems,
    selectedIds: providedSelectedIds,
    onSelectionChange,
    itemRenderer,
    onOrderChange,
    onItemChanged,
    showMoveButtons = true,
    groupField,
    groupRenderer,
    handleRenderer,
    allGroupKeys,
}) => {


    const internalListID = useMemo(() => {
        return newGuid();
    }, []);

    const [items, setItems] = useState(initialItems ?? []);
    const [recentlyDroppedIds, setRecentlyDroppedIds] = useState([]);
    // Maintain an internal selection state when none is provided
    const [internalSelectedIds, setInternalSelectedIds] = useState([]);
    const draggedGroupRef = useRef(null);
    const containerRef = useRef(null);

    const onDropItem = useCallback((dropIndex, targetGroupKey) => {
        if (!draggedGroupRef.current) return;

        const groupIndices = draggedGroupRef.current;
        const groupItems = groupIndices.map(i => ({
            ...items[i],
            [groupField]: targetGroupKey,
        }));
        
        const groupIds = groupIndices.map(i => items[i][idField]);
        const remainingItems = items.filter(item => !groupIds.includes(item[idField]));

        const offset = groupIndices.filter(i => i < dropIndex).length;
        const adjustedIndex = dropIndex - offset;
        const finalIndex = Math.max(0, Math.min(adjustedIndex, remainingItems.length));

        remainingItems.splice(finalIndex, 0, ...groupItems);

        setItems(remainingItems);
        setRecentlyDroppedIds(groupItems.map(item => item[idField]));
        draggedGroupRef.current = null;

        if (providedSelectedIds === undefined) {
            setInternalSelectedIds([]);
        }

        if (onSelectionChange) {
            onSelectionChange([]);
        }

        if (onOrderChange) {
            onOrderChange(remainingItems);
        }

        setTimeout(() => {
            const firstDroppedId = groupItems[0]?.[idField];
            if (!firstDroppedId) return;
            const droppedElem = document.getElementById(`${idField}-${firstDroppedId}`);
            if (droppedElem && containerRef.current) {
                droppedElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);

        setTimeout(() => {
            setRecentlyDroppedIds([]);
        }, 2000);
    }, [items, onOrderChange, idField, groupField, onSelectionChange, providedSelectedIds]);

    const handleDropIntoGroup = useCallback((groupKey) => {
        if (!draggedGroupRef.current) return;

        const groupIndices = draggedGroupRef.current;
        const groupIds = groupIndices.map(i => items[i][idField]);

        const groupItems = items
            .filter(item => groupIds.includes(item[idField]))
            .map(item => ({ ...item, [groupField]: groupKey }));

        const remainingItems = items.filter(item => !groupIds.includes(item[idField]));

        // Find the index of the target group in allGroupKeys
        const groupIndex = allGroupKeys ? allGroupKeys.indexOf(groupKey) : -1;
        let insertIndex = remainingItems.length; // default to end

        if (groupIndex !== -1 && allGroupKeys) {
            // Find the next non-empty group after the target group
            for (let i = groupIndex + 1; i < allGroupKeys.length; i++) {
                const nextGroupKey = allGroupKeys[i];
                const nextGroupItemIndex = remainingItems.findIndex(item => item[groupField] === nextGroupKey);
                if (nextGroupItemIndex !== -1) {
                    insertIndex = nextGroupItemIndex;
                    break;
                }
            }
        }

        const finalItems = [...remainingItems];
        finalItems.splice(insertIndex, 0, ...groupItems);

        setItems(finalItems);
        draggedGroupRef.current = null;

        if (onOrderChange) onOrderChange(finalItems);
        setRecentlyDroppedIds(groupIds);
        setTimeout(() => setRecentlyDroppedIds([]), 2000);
        if (providedSelectedIds === undefined) setInternalSelectedIds([]);
        if (onSelectionChange) onSelectionChange([]);
    }, [items, idField, groupField, onOrderChange, onSelectionChange, providedSelectedIds, allGroupKeys]);

    const itemIndexMap = useMemo(() => {
        const map = new Map();
        items.forEach((item, index) => {
            map.set(item[idField], index);
        });
        return map;
    }, [items, idField]);

    // Use provided selection if available, otherwise use internal state
    const selectedIds = useMemo(
        () => providedSelectedIds !== undefined ? providedSelectedIds : internalSelectedIds,
        [providedSelectedIds, internalSelectedIds]
    );

    useEffect(() => {
        setItems(initialItems ?? []);
    }, [initialItems]);

    const toggleSelect = useCallback((id) => {
        const newSelectedIds = selectedIds.includes(id)
            ? selectedIds.filter((itemId) => itemId !== id)
            : [...selectedIds, id];

        // Update internal state if no external state is provided
        if (providedSelectedIds === undefined) {
            setInternalSelectedIds(newSelectedIds);
        }

        // Always call the callback if provided
        if (onSelectionChange) {
            onSelectionChange(newSelectedIds);
        }
    }, [selectedIds, onSelectionChange, providedSelectedIds]);

    const onDragStartItem = useCallback(
        (index, isSelected) => {
            if (isSelected) {
                const groupIndices = items.reduce((acc, item, idx) => {
                    if (selectedIds.includes(item[idField])) acc.push(idx);
                    return acc;
                }, []);
                draggedGroupRef.current = groupIndices;
            } else {
                draggedGroupRef.current = [index];
                const newSelection = [items[index][idField]];

                // Update internal state if no external state is provided
                if (providedSelectedIds === undefined) {
                    setInternalSelectedIds(newSelection);
                }

                if (onSelectionChange) {
                    onSelectionChange(newSelection);
                }
            }
        },
        [items, selectedIds, idField, onSelectionChange, providedSelectedIds]
    );

    const onDragEnd = useCallback(() => {
    }, []);

    // Move functions that operate on all selected items if any; otherwise, use the triggered item.
    const moveUp = (triggerIndex) => {
        const groupIds =
            selectedIds.length > 0 ? selectedIds : [items[triggerIndex][idField]];
        const newItems = [...items];
        for (let i = 1; i < newItems.length; i++) {
            if (
                groupIds.includes(newItems[i][idField]) &&
                !groupIds.includes(newItems[i - 1][idField])
            ) {
                const temp = newItems[i - 1];
                newItems[i - 1] = newItems[i];
                newItems[i] = temp;
            }
        }
        setItems(newItems);
        if (onOrderChange) onOrderChange(newItems);
    };

    const moveDown = (triggerIndex) => {
        const groupIds =
            selectedIds.length > 0 ? selectedIds : [items[triggerIndex][idField]];
        const newItems = [...items];
        for (let i = newItems.length - 2; i >= 0; i--) {
            if (
                groupIds.includes(newItems[i][idField]) &&
                !groupIds.includes(newItems[i + 1][idField])
            ) {
                const temp = newItems[i + 1];
                newItems[i + 1] = newItems[i];
                newItems[i] = temp;
            }
        }
        setItems(newItems);
        if (onOrderChange) onOrderChange(newItems);
    };

    const moveToTop = (triggerIndex) => {
        const groupIds =
            selectedIds.length > 0 ? selectedIds : [items[triggerIndex][idField]];
        const groupItems = items.filter((item) => groupIds.includes(item[idField]));
        const remainingItems = items.filter((item) => !groupIds.includes(item[idField]));
        const newItems = [...groupItems, ...remainingItems];
        setItems(newItems);
        if (onOrderChange) onOrderChange(newItems);
    };

    const moveToBottom = (triggerIndex) => {
        const groupIds =
            selectedIds.length > 0 ? selectedIds : [items[triggerIndex][idField]];
        const groupItems = items.filter((item) => groupIds.includes(item[idField]));
        const remainingItems = items.filter((item) => !groupIds.includes(item[idField]));
        const newItems = [...remainingItems, ...groupItems];
        setItems(newItems);
        if (onOrderChange) onOrderChange(newItems);
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const threshold = 50;
        const scrollSpeed = 20;
        const handleMouseMove = (e) => {
            const rect = container.getBoundingClientRect();
            if (e.clientY < rect.top + threshold) {
                container.scrollTop -= scrollSpeed;
            } else if (e.clientY > rect.bottom - threshold) {
                container.scrollTop += scrollSpeed;
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    const renderListItem = (item, index) => (
        <ReorderableListItem
            key={item[idField]}
            idField={idField}
            index={index}
            item={item}
            toggleSelect={toggleSelect}
            selected={selectedIds.includes(item[idField])}
            onDragStartItem={onDragStartItem}
            onDragEnd={onDragEnd}
            onDropItem={onDropItem}
            itemRenderer={itemRenderer}
            onItemChanged={onItemChanged}
            recentlyDropped={recentlyDroppedIds.includes(item[idField])}
            onMoveUp={() => moveUp(index)}
            onMoveDown={() => moveDown(index)}
            onMoveToTop={() => moveToTop(index)}
            onMoveToBottom={() => moveToBottom(index)}
            showMoveButtons={showMoveButtons}
            listID={internalListID}
            handleRenderer={handleRenderer}
            groupField={groupField}
        />
    );

    const content = (accentUtils.isNull(groupField) || accentUtils.isNull(groupRenderer) ?
        items.map((item, index) => renderListItem(item, index)) :
        (() => {
            const itemGroups = from(items).groupBy(x => x[groupField]).toDictionary(x => x.key(), x => x.getSource());
            const keys = allGroupKeys || itemGroups.keys();

            return from(keys).select(key => {
                const group = itemGroups.get(key) || [];
                const renderedItems = group.map(item => renderListItem(item, itemIndexMap.get(item[idField])));
                const DropTarget = () => (
                    <EmptyGroupDropTarget onDrop={() => handleDropIntoGroup(key)} listID={internalListID} />
                );
                return groupRenderer(key, renderedItems, DropTarget);
            }).toArray();
        })());


    return (
        <DndProvider backend={HTML5Backend}>
            <div ref={containerRef}>
                {content}
            </div>
        </DndProvider>
    );
};